import i18next from 'i18next';

// Separator between ancestor titles: a text chevron rather than an icon, so the
// path stays a single string that ellipsizes, copies and fits in a tooltip. The
// view configurator's column preview joins through formatPathSegments below, so
// both surfaces read identically.
var PATH_SEPARATOR = ' › ';

// API attribute tokens mapped to their existing i18n keys. Tokens outside this
// map render nothing rather than leaking a raw API value into the header.
var ATTRIBUTE_LABEL_KEYS = {
  user: 'settings:fieldAttribute_user',
  timestamp: 'settings:fieldAttribute_timestamp',
};

// Each header line is measured in the type it actually renders in, so the width
// a column reserves is the width its content needs -- a proxy font mis-sizes by
// a share of the string, which shows up as a column that clips its own title or
// as dead space trailing the action icons. Keep these in step with the rules
// they mirror: .header-cell-title, .preview-table-column-hierarchy and
// .header-cell-attribute in src/css/styles.css.
var FONT_STACK = 'Inter, -apple-system, system-ui, sans-serif';

var TITLE_LINE = {
  font: '500 11px ' + FONT_STACK,
  letterSpacing: 0.55,
  uppercase: true,
};
var PATH_LINE = {
  font: '500 9px ' + FONT_STACK,
  letterSpacing: 0.2,
  uppercase: false,
};
var ATTRIBUTE_LINE = {
  font: '500 10px ' + FONT_STACK,
  letterSpacing: 0.1,
  uppercase: false,
};

// The 5px gap in .header-cell-title-row.
var ATTRIBUTE_GAP = 5;

// The attribute chip's own box: 6px padding and a 1px border either side.
var ATTRIBUTE_CHIP_BOX = 14;

/**
 * True when the column carries a resolved ancestor path.
 *
 * The API guarantees `path` is always an array once the contract change ships,
 * but it is absent on the shape sent today, so presence is checked too.
 */
export function hasColumnPath(column) {
  return !!(column && Array.isArray(column.path) && column.path.length > 0);
}

/**
 * True when any column in a header row has a resolved path.
 *
 * Drives the row-level spacer rule: a view whose columns all report an empty
 * path renders exactly as it did before this feature, with no reserved gap.
 */
export function rowHasColumnPath(columns) {
  if (!columns || !columns.length) return false;
  for (var i = 0; i < columns.length; i++) {
    if (hasColumnPath(columns[i])) return true;
  }
  return false;
}

/**
 * Ancestor titles joined for display, outermost first.
 *
 * Takes the raw segment array so the configurator preview, which carries its
 * path as `hierarchyPath`, joins through the same separator as the table header.
 */
export function formatPathSegments(segments) {
  if (!segments || !segments.length) return '';
  return segments.join(PATH_SEPARATOR);
}

/**
 * The column's ancestor titles as a display string, outermost first.
 * Empty string when the column sits at the top level or its path is unresolved.
 */
export function formatColumnPath(column) {
  if (!hasColumnPath(column)) return '';
  return formatPathSegments(column.path);
}

/**
 * The translated label for an attribute token.
 *
 * Empty for a missing token, for `value` (every field column has a value, so
 * labelling them all would be noise), and for tokens outside the map, which
 * render nothing rather than leaking a raw API value into the UI.
 *
 * Shared with the configurator preview, whose fields carry the same tokens
 * under `fieldAttribute` (see config/constants.js fieldAttributeOptions).
 */
export function formatAttributeLabel(attribute, t) {
  if (!attribute) return '';
  var key = ATTRIBUTE_LABEL_KEYS[attribute];
  if (!key) return '';
  var translate = t || i18next.t.bind(i18next);
  return translate(key);
}

/**
 * The translated label for the column's attribute.
 *
 * Empty for metadata columns, which have no `attribute` property at all.
 */
export function formatColumnAttribute(column, t) {
  if (!column) return '';
  return formatAttributeLabel(column.attribute, t);
}

/**
 * One rendered header line's width in CSS pixels.
 *
 * Canvas text measurement applies neither `text-transform` nor `letter-spacing`,
 * so the title is uppercased here and the tracking is added back -- CSS lays it
 * out after every character, the last one included.
 */
function measureLine(text, line, measureText) {
  if (!text) return 0;
  var value = line.uppercase ? text.toUpperCase() : text;
  return measureText(value, line.font) + value.length * line.letterSpacing;
}

/**
 * Width the header content needs to render untruncated, in CSS pixels.
 *
 * The path sits on its own line above the title, so the content is whichever of
 * the two lines is wider; the attribute chip shares the title's line and never
 * shrinks, so its label and its own box add to that line. This covers the
 * content only -- the header chrome around it is ScrollableTable's
 * HEADER_CHROME_WIDTH.
 *
 * `measureText` is injected -- canvasHelper's getTextWidth in the app -- so the
 * calculation stays testable without a canvas.
 */
export function measureColumnHeaderWidth(column, measureText, t) {
  if (!column) return 0;

  var titleLine = measureLine(column.name || '', TITLE_LINE, measureText);
  var attributeLabel = formatColumnAttribute(column, t);
  if (attributeLabel) {
    titleLine +=
      ATTRIBUTE_GAP +
      ATTRIBUTE_CHIP_BOX +
      measureLine(attributeLabel, ATTRIBUTE_LINE, measureText);
  }

  var pathLine = measureLine(formatColumnPath(column), PATH_LINE, measureText);

  return Math.max(titleLine, pathLine);
}

/**
 * The column header as a single line of plain text, for contexts that cannot
 * render the two-line header (clipboard export, native tooltips).
 */
export function formatColumnHeaderText(column, t) {
  if (!column) return '';
  var name = column.name || '';
  var attributeLabel = formatColumnAttribute(column, t);
  if (!attributeLabel) return name;
  return name + ' (' + attributeLabel + ')';
}
